<template>
  <!-- Contract highlights -->
  <BlockContainer class="p-4 flex-fill">
    <ValidationObserver ref="contractHighlightsForm" v-slot="{ invalid, dirty }">
      <div class="row align-items-center">
        <div class="col">
          <h5>Kontraktsdetaljer</h5>
        </div>

        <div class="col edit-buttons text-right">
          <div v-if="editMode">
            <CancelButton class="mr-1" @click.native="editMode = false" />
            <SaveButton
              @click.native="saveAndCloseEdit"
              :loading="loading.post"
              :disabled="!dirty || invalid || loading.post"
              class="ml-1"
            />
          </div>

          <EditButton
            v-else-if="getTenancy.subscription.subscriptionPlanName !== 'Garantert' || isUserSudo"
            @click.native="setEditModeAndLocalCopy"
            :class="{ sudo: getTenancy.subscription.subscriptionPlanName === 'Garantert' && isUserSudo }"
          />
        </div>
      </div>

      <!-- Choose contract duration -->
      <ValidationProvider>
        <div v-if="editMode" class="row mt-5">
          <div class="col-6">
            <hl-RadioButtonTapWithIcon iconClass="budicon-hourglass" v-model="durationType" radioValue="limited">
              <p>Leieforholdet er</p>
              <p class="font-weight-bold">tidsbestemt</p>
            </hl-RadioButtonTapWithIcon>
          </div>

          <div class="col-6">
            <hl-RadioButtonTapWithIcon iconClass="budicon-forward-time" v-model="durationType" radioValue="unlimited">
              <p>Leieforholdet er</p>
              <p class="font-weight-bold">tidsubestemt</p>
            </hl-RadioButtonTapWithIcon>
          </div>
        </div>
      </ValidationProvider>
      <!-- / Choose contract duration -->

      <div v-if="getTenancy">
        <div class="row mt-4">
          <div class="col">
            <div v-if="editMode">
              <ValidationProvider
                name="Startdato"
                :rules="validationRules.tenancy.newTenancyForm.contractDetails.startDate"
                v-slot="{ errors }"
              >
                <hl-Datepicker
                  label="Startdato"
                  v-model="localCopy.startDate"
                  name="rent.startDate"
                  :error="errors[0]"
                ></hl-Datepicker>
              </ValidationProvider>
            </div>
            <div v-else>
              <p class="m-0">Startdato</p>
              <h6>
                <hl-Date :date="getTenancy.startDate"></hl-Date>
              </h6>
            </div>
          </div>

          <div class="col">
            <div v-if="editMode && durationType === 'limited'">
              <ValidationProvider name="Sluttdato" :rules="'required'" v-slot="{ errors }">
                <hl-Datepicker
                  label="Sluttdato"
                  v-model="localCopy.endDate"
                  name="rent.startDate"
                  :error="errors[0]"
                ></hl-Datepicker>
              </ValidationProvider>
            </div>
            <div v-else-if="!editMode">
              <p class="m-0">Sluttdato</p>
              <h6>
                <hl-Date v-if="getTenancy.endDate" :date="getTenancy.endDate"></hl-Date>
                <h6 v-else>n/a</h6>
              </h6>
            </div>
          </div>
        </div>

        <div class="row mt-3">
          <div class="col">
            <div v-if="editMode">
              <ValidationProvider
                name="Bindingstid"
                :rules="validationRules.tenancy.newTenancyForm.contractDetails.bindingPeriod"
                v-slot="{ errors }"
              >
                <hl-Select
                  label="Bindingstid"
                  placeholder="Velg bindingstid"
                  v-model="localCopy.details.bindingPeriod"
                  :options="bindingstidOptions"
                  :error="errors[0]"
                />
              </ValidationProvider>
            </div>

            <div v-else>
              <p class="m-0">Bindingstid</p>
              <h6 v-if="!getTenancy.details.bindingPeriod || getTenancy.details.bindingPeriod === 0">
                Ingen bindingstid
              </h6>
              <h6 v-else>
                {{ getTenancy.details.bindingPeriod }}
                {{ getTenancy.details.bindingPeriod === '1' ? 'måned' : 'måneder' }}
              </h6>
            </div>
          </div>

          <div class="col">
            <div v-if="editMode">
              <!-- v-model="localNoticePeriod"  -->
              <ValidationProvider
                name="Oppsigelsestid"
                :rules="validationRules.tenancy.newTenancyForm.contractDetails.noticePeriod"
                v-slot="{ errors }"
              >
                <hl-Select
                  label="Oppsigelsestid"
                  placeholder="Velg oppsigelsestid"
                  v-model="localCopy.details.noticePeriod"
                  :options="oppsigelsestidOptions"
                  :error="errors[0]"
                />
              </ValidationProvider>
            </div>
            <div v-else>
              <p class="m-0">Oppsigelsestid</p>
              <h6 v-if="!getTenancy.details.noticePeriod || getTenancy.details.noticePeriod === 0">
                Ingen oppsigelsestid
              </h6>
              <h6 v-else>
                {{ getTenancy.details.noticePeriod }} {{ getTenancy.details.noticePeriod === 1 ? 'måned' : 'måneder' }}
              </h6>
            </div>
          </div>
        </div>

        <div class="row mt-3">
          <div class="col">
            <div v-if="!editMode">
              <p class="mb-1">Status</p>

              <hl-Tag :variation="tenancyStatusMap[getTenancyStatus].variation">{{
                tenancyStatusMap[getTenancyStatus].value
              }}</hl-Tag>
            </div>
          </div>

          <div class="col">
            <div v-if="editMode">
              <ValidationProvider
                name="Betalingsfrekvens"
                :rules="validationRules.tenancy.newTenancyForm.contractDetails.invoicePeriod"
                v-slot="{ errors }"
              >
                <hl-Select
                  label="Betalingsfrekvens"
                  placeholder="Velg periode"
                  v-model="localCopy.rent.invoiceInterval"
                  :options="invoicePeriodOptions"
                  :error="errors[0]"
                />
              </ValidationProvider>
            </div>

            <div v-else>
              <p class="m-0">Betalingsfrekvens</p>
              <h6>{{ invoiceIntervalMap[getTenancy.rent.invoiceInterval] }}</h6>
            </div>
          </div>
        </div>

        <div class="row mt-3">
          <div class="col">
            <div v-if="editMode">
              <ValidationProvider
                name="Forfallsdag"
                :rules="validationRules.tenancy.newTenancyForm.contractDetails.dueDay"
                v-slot="{ errors }"
              >
                <hl-Select
                  label="Forfallsdag"
                  placeholder="Velg forfallsdag"
                  v-model="localCopy.rent.dueDay"
                  :options="dueDayOptions"
                  :error="errors[0]"
                />
              </ValidationProvider>
            </div>

            <div v-else>
              <p class="m-0">Forfallsdag</p>
              <h6>{{ getTenancy.rent.dueDay }}</h6>
            </div>
          </div>

          <div class="col">
            <div v-if="editMode">
              <ValidationProvider
                name="Husleieperiode"
                :rules="validationRules.tenancy.newTenancyForm.contractDetails.duePeriod"
                v-slot="{ errors }"
              >
                <hl-Select
                  label="Husleieperiode"
                  placeholder="Velg husleieperiode"
                  v-model="localCopy.rent.duePeriod"
                  :options="rentPeriodOptions"
                  :disabled="localCopy.rent.invoiceInterval === 'quarter'"
                  :error="errors[0]"
                />
              </ValidationProvider>
            </div>

            <div v-else>
              <p class="m-0">Husleieperiode</p>
              <h6>{{ rentPeriodMap[getTenancy.rent.duePeriod] }}</h6>
            </div>
          </div>
        </div>
      </div>
    </ValidationObserver>
  </BlockContainer>
  <!-- / Contract highlights -->
</template>

<script>
import BlockContainer from '@/components/BlockContainer';
import CancelButton from '@/components/UI/buttons/CancelButton';
import EditButton from '@/components/UI/buttons/EditButton';
import SaveButton from '@/components/UI/buttons/SaveButton';
import addValidationRules from '@/mixins/addValidationRules.js';
import Datepicker from '@modules/husleie-framework/src/components/forms/inputs/Datepicker.vue';
import RadioButtonTapWithIcon from '@modules/husleie-framework/src/components/forms/inputs/radio-buttons/RadioButtonTapWithIcon.vue';
import Select from '@modules/husleie-framework/src/components/forms/inputs/select/Select.vue';
import Tag from '@modules/husleie-framework/src/components/forms/inputs/tags/Tag.vue';
import Date from '@modules/husleie-framework/src/components/time/Date.vue';
import i18nCommon from '@modules/i18n/mixins/Common';
import { mapGetters, mapActions } from 'vuex';

export default {
  mixins: [i18nCommon, addValidationRules],
  components: {
    BlockContainer,
    CancelButton,
    EditButton,
    SaveButton,
    'hl-Datepicker': Datepicker,
    'hl-RadioButtonTapWithIcon': RadioButtonTapWithIcon,
    'hl-Date': Date,
    'hl-Select': Select,
    'hl-Tag': Tag,
  },

  props: ['landlordId', 'tenancyId'],

  data() {
    return {
      editMode: false,
      localCopy: {},
      loading: {
        get: false,
        post: false,
      },
      durationType: '',
      tenancyStatusMap: {
        free: {
          value: 'Gratis',
          variation: 'primary',
        },
        active: {
          value: 'Aktivt',
          variation: 'primary',
        },
        'in-cancellation': {
          value: 'I avslutning',
          variation: 'warning',
        },
        cancelled: {
          value: 'Avsluttet',
          variation: 'danger',
        },
      },
      bindingstidOptions: [
        { name: 'Ingen bindingstid', value: '0' },
        { name: '1 måned', value: 1 },
        { name: '2 måneder', value: 2 },
        { name: '3 måneder', value: 3 },
        { name: '4 måneder', value: 4 },
        { name: '5 måneder', value: 5 },
        { name: '6 måneder', value: 6 },
        { name: '7 måneder', value: 7 },
        { name: '8 måneder', value: 8 },
        { name: '9 måneder', value: 9 },
        { name: '10 måneder', value: 10 },
        { name: '11 måneder', value: 11 },
        { name: '12 måneder', value: 12 },
      ],
      oppsigelsestidOptions: [
        { name: 'Ingen oppsigelsestid', value: 0 },
        { name: 'Egendefinert', value: 'egendefinert' },
        { name: '1 måned', value: 1 },
        { name: '2 måneder', value: 2 },
        { name: '3 måneder', value: 3 },
        { name: '4 måneder', value: 4 },
        { name: '5 måneder', value: 5 },
        { name: '6 måneder', value: 6 },
        { name: '7 måneder', value: 7 },
        { name: '8 måneder', value: 8 },
        { name: '9 måneder', value: 9 },
        { name: '10 måneder', value: 10 },
        { name: '11 måneder', value: 11 },
        { name: '12 måneder', value: 12 },
      ],

      dueDayOptions: [
        { name: '1', value: 1 },
        { name: '5', value: 5 },
        { name: '10', value: 10 },
        { name: '15', value: 15 },
        { name: '20', value: 20 },
        { name: '25', value: 25 },
      ],

      rentPeriodOptions: [
        { name: 'neste måned (forskudd)', value: 'advance' },
        { name: 'inneværende måned', value: 'current' },
        { name: 'forrige måned (etterskudd)', value: 'postpay' },
      ],

      rentPeriodMap: {
        advance: 'neste måned (forskudd)',
        current: 'inneværende måned',
        postpay: 'forrige måned (etterskudd)',
      },

      invoicePeriodOptions: [
        { name: 'måned', value: 'month' },
        { name: 'kvartal', value: 'quarter' },
      ],

      invoiceIntervalMap: {
        month: 'måned',
        quarter: 'kvartal',
      },
    };
  },

  computed: {
    ...mapGetters(['isUserSudo']),
    ...mapGetters('tenancy', ['getTenancy', 'getTenancyStatus', 'getTenancyPaymentInfo']),
    translationErrorCouldNotSaveRental() {
      return this.translate('error.could not save rental');
    },
  },

  watch: {
    durationType(v) {
      if (v === 'unlimited') {
        this.localCopy.endDate = null;
      }
      this.$nextTick(() => {
        this.$refs.contractHighlightsForm.validate();
      });
    },
    'localCopy.rent.invoiceInterval'(newVal) {
      if (newVal === 'quarter') {
        this.localCopy.rent.duePeriod = 'current';
      }
    },
  },

  methods: {
    ...mapActions('tenancy', ['updateTenancy', 'updateTenancyRent']),

    setEditModeAndLocalCopy() {
      if (this.getTenancy == null) return;
      this.$set(this, 'localCopy', JSON.parse(JSON.stringify(this.getTenancy)));
      console.log('hereeee', this.getTenancy.endDate);
      if (this.getTenancy.endDate == null) {
        this.durationType = 'unlimited';
      } else {
        this.durationType = 'limited';
      }
      this.editMode = true;
      this.$nextTick(() => {
        this.$refs.contractHighlightsForm.validate();
      });
    },

    async saveTenancy(landlordId, tenancyId) {
      const tenancy = JSON.parse(JSON.stringify(this.localCopy));

      // delete readOnly keys from tenancy Object
      delete tenancy.id;
      delete tenancy.rentalObjectId;
      delete tenancy.hasDeposits;
      delete tenancy.rent;
      delete tenancy.tenants;
      delete tenancy.subProducts;
      delete tenancy.rentAdjustment;
      delete tenancy.subscription;
      try {
        await this.updateTenancy({
          landlordId,
          tenancyId,
          data: tenancy,
        });
      } catch (error) {
        console.error(error);
        this.$toast(this.translationErrorCouldNotSaveRental, 'error');
      }
    },

    async saveTenancyRent(landlordId, tenancyId) {
      const tenancy = JSON.parse(JSON.stringify(this.localCopy));
      try {
        await this.updateTenancyRent({
          landlordId,
          tenancyId,
          data: tenancy.rent,
        });
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke lagre leie.', 'error');
      }
    },

    async saveAndCloseEdit() {
      this.loading.post = true;
      const [landlordId, tenancyId] = [this.landlordId, this.tenancyId];
      try {
        await Promise.all([this.saveTenancy(landlordId, tenancyId), this.saveTenancyRent(landlordId, tenancyId)]);
        this.loading.post = false;
        this.editMode = false;
      } catch (error) {
        console.error(error);
      }
      this.loading.post = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.edit-buttons {
  flex: 0 0 200px;
}
.sudo {
  border: 2px solid #ed2135;
}
</style>
